/**
 * N165 — quando o alerta de intervalo de agendamento deve aparecer.
 *
 * O PROBLEMA: o modal de "Controle de intervalo" (mín. 15 / máx. 30 dias entre
 * contagens do mesmo cliente no mesmo hospital) aparecia a CADA gravação da
 * contagem — incluir lacre, mexer em ordem de coleta, ajustar horário, qualquer
 * coisa. A condição nos dois formulários só checava que os campos EXISTIAM:
 *
 *     if (selectedClienteIds.length > 0 && selectedHospitalId && dataPrevista)
 *
 * Como toda gravação manda a data, toda gravação revalidava. Alerta que aparece
 * sempre deixa de ser lido — e aí o dia em que ele importa passa batido.
 *
 * A REGRA vive AQUI, numa função só, porque o defeito nasceu duas vezes: a mesma
 * condição foi escrita em `useContagemSave` e em `useJobForm`. Consertar nos dois
 * lugares é garantir que voltem a divergir.
 */

export interface ContextoValidacaoIntervalo {
  /** Contagem nova (primeiro agendamento). */
  isNew: boolean;
  /** A data de agendamento mudou nesta edição. */
  dataMudou: boolean;
  /** O hospital mudou nesta edição. */
  hospitalMudou: boolean;
  /** Clientes selecionados agora. */
  clientesAtuais: number[];
  /** Clientes que a contagem já tinha quando foi carregada. */
  clientesOriginais: number[];
}

/**
 * Quais clientes precisam ter o intervalo validado nesta gravação.
 *
 * Lista vazia = NÃO chamar a API e NÃO abrir o modal.
 *
 * | Situação                                   | Quem valida            |
 * |--------------------------------------------|------------------------|
 * | contagem nova                              | todos os selecionados  |
 * | data de agendamento mudou                  | todos                  |
 * | hospital mudou                             | todos                  |
 * | cliente novo incluído, sem mexer na data   | só o cliente incluído  |
 * | lacre / horário / ordem de coleta / status | ninguém                |
 *
 * Duas decisões do dono estão embutidas:
 *
 * - **Horário não conta.** A regra é medida em DIAS; mudar de 08:00 para 14:00
 *   não altera intervalo nenhum.
 * - **Cliente incluído valida só ele.** Os que já estavam na contagem foram
 *   conferidos quando entraram — reavaliá-los é justamente o ruído que se quer
 *   eliminar.
 *
 * Hospital entra junto porque a regra é por PAR cliente+hospital: trocar o
 * hospital muda o par de todo mundo, então todos precisam ser reavaliados.
 *
 * Remover cliente devolve lista vazia: tirar contagem de um cliente nunca viola
 * intervalo mínimo/máximo.
 */
export function clientesParaValidarIntervalo(
  ctx: ContextoValidacaoIntervalo
): number[] {
  const atuais = ctx.clientesAtuais ?? [];

  if (atuais.length === 0) return [];

  if (ctx.isNew || ctx.dataMudou || ctx.hospitalMudou) {
    return [...atuais];
  }

  const originais = new Set(ctx.clientesOriginais ?? []);
  return atuais.filter((id) => !originais.has(id));
}
